import React, { useEffect } from 'react';
import {
  ProductCatalogue,
  FilterGroup,
  Product
} from '@common-modules/product-catalogue';
import { accessoryActions, planActions } from '@detox/actions';
import { Grid, Row, Column, Spacing, StatusAlert } from '@dls/web';
import { KeyValue } from '../../types/common.types';
import { flattenNodes, getAemConfigs } from '../../helpers/common';
import { useDispatch, useSelector } from 'react-redux';
import {
  ProductCatalogState,
  setFilterData,
  applyFilter as actionApplyFilter,
  clearFilter as actionClearFilter,
  selectFilter as actionSelectFilter,
  resetToPreviousFilter as actionResetToPreviousFilter,
  setProductList,
  setIppFilter,
  setRRPFlow
} from '../../reducers/productCatalogs';
import { BANNER_FLOW_TYPE, Plan } from '../../types/plan.types';
import { navigate } from 'gatsby';
import {
  checkIppChanged,
  getDefaultPhonePlan,
  getSelectedPlan,
  getSimOnlyPlans,
  setUrlParamsMapping,
  updateUrlParamFromFilter
} from '../../helpers/productCatalogHelper';
import { getUrlParams } from '@wec-core/helpers';
import {
  getPhoneCatalogBanner,
  getUrlByPlan
} from '../../helpers/catalogue-helpers';
import { CATALOGUE } from '../../constants';
import getAemComponentByName from '../../helpers/getAemComponentByName';
import PromoBanner from '../PromoBanner';
import { GatsbyImage } from 'gatsby-plugin-image';
import { UserInformation } from '../../types/user.types';
import CisPointOfEntry from '../CisPointOfEntry';
import isSimOnlyPlan from '../../helpers/is-sim-only-plan';
import { trans as t } from '../../helpers/localisation';
import { useInit } from '../../hooks/useInit';
import { BannerList } from '../RrpBanner';
import isFeatureFlagEnabled from '../../helpers/feature-flags';
import { NEW_PROMO_BANNER } from '../../types/featureFlag.types';
import CONSTANTS from '../../constants/common';
import { AEM_CONFIG_KEYS } from '../../config/common';
import { getDefaultPlan } from '../../helpers/productDetails';
import { FullWidthDiv } from '../../styles/common';
type PropTypes = {
  data: KeyValue;
  isCisPage: boolean;
  location: Location;
  previewMode: boolean;
};

export const PhoneCatalog = (props: PropTypes) => {
  const { data, previewMode, isCisPage } = props;

  const dispatch = useDispatch();

  const {
    productCatalog,
    selectedPlan,
    cisRates,
    cisLoading,
    userSelection,
    selectedProduct
  } = useSelector((state: KeyValue) => {
    return {
      productCatalog: state.productCatalog as ProductCatalogState,
      selectedPlan: state.plan?.selectedPlan as Plan,
      userSelection: state.user?.selection,
      cisRates: (state.user as UserInformation)?.cis?.information?.rates,
      cisLoading: (state.user as UserInformation)?.cis?.loading,
      selectedProduct: state.product?.selectedProduct
    };
  });

  const {
    allPhoneCatalogCategory,
    allPhoneBrand,
    allPhone,
    allPlan,
    allAemPages,
    allInstalmentOptions,
    allPhoneBanner
  } = data;
  const plans = flattenNodes(allPlan) as Plan[];
  const brands = flattenNodes(allPhoneBrand);
  const categories = flattenNodes(allPhoneCatalogCategory);
  const ippOptions = flattenNodes(allInstalmentOptions);
  const products = flattenNodes(allPhone);
  const banners = flattenNodes(allPhoneBanner);
  const getQueryParams = () => {
    return getUrlParams(window.location.search);
  };
  const allVariableConfig = flattenNodes(data?.allVariableConfig || []);
  const [defaultPlanConfig] = data?.allVariableConfig
    ? getAemConfigs(allVariableConfig, [AEM_CONFIG_KEYS.DETOX_DEFAULT_PLANS])
    : [''];
  const defaultPlanIds = defaultPlanConfig?.split(',');

  useEffect(() => {
    dispatch(setRRPFlow(false));
    setUrlParamsMapping([categories, ippOptions]);
  }, []);

  useEffect(() => {
    if (!productCatalog.filterData?.length && !selectedPlan?.planId) {
      dispatch(setIppFilter(getQueryParams()?.[CATALOGUE.FILTER_TYPES.PLAN]));
    }
  }, []);

  useEffect(() => {
    if (selectedPlan) {
      dispatch(
        setProductList({
          products,
          selectedPlan,
          simOnlyPlans: getSimOnlyPlans(plans),
          queryParams: getQueryParams() as KeyValue,
          filterData: productCatalog.filterData
        })
      );
    }
  }, [selectedPlan]);

  useInit(() => {
    if (
      productCatalog.productList?.length &&
      !productCatalog.filterData.length
    ) {
      dispatch(
        setFilterData({
          brands,
          categories,
          queryParams: getQueryParams() as KeyValue,
          ippOptions,
          selectedPlan
        })
      );
    }
  }, [productCatalog.productList.length]);

  useEffect(() => {
    if (productCatalog.filterData?.length) {
      updateUrlParamFromFilter(
        productCatalog.filterData,
        selectedPlan,
        getQueryParams() as KeyValue
      );
    }
  }, [productCatalog.filterData]);

  useEffect(() => {
    if (productCatalog.isDoneResetFilter) {
      const plan = getDefaultPhonePlan(plans, cisRates, defaultPlanIds);
      selectPlanAndUpdateUrl(plan);
    }
  }, [productCatalog.isDoneResetFilter]);

  useEffect(() => {
    if (productCatalog.hasIppFilter !== null) {
      const plan = getPlanWhenChangeIpp(productCatalog.hasIppFilter);
      selectPlanAndUpdateUrl(plan);
    }
  }, [productCatalog.hasIppFilter]);

  const getPlanWhenChangeIpp = hasIppFilter => {
    const isSimOnlySelected = isSimOnlyPlan(userSelection?.plan);
    if (hasIppFilter) {
      const simOnlyPlan = isSimOnlySelected
        ? userSelection.plan
        : getDefaultPlan(defaultPlanIds, getSimOnlyPlans(plans));
      return {
        ...simOnlyPlan,
        selectedMonthlyTerm:
          selectedPlan?.selectedMonthlyTerm || CATALOGUE.INSTALLMENT_MONTHS[0]
      };
    } else {
      return getSelectedPlan({
        plans,
        location: window.location,
        queryParams: getQueryParams() as KeyValue,
        fromQueryParams: false,
        cisRates,
        selectedPlan: !isSimOnlySelected && userSelection?.plan,
        defaultPlanIds
      });
    }
  };

  const selectPlanAndUpdateUrl = plan => {
    const url = getUrlByPlan(plan);
    if (url) {
      updateUrlParamFromFilter(
        productCatalog.filterData,
        plan,
        getQueryParams() as KeyValue
      );
      dispatch(planActions.setSelectedPlan(plan));
    }
  };

  const applyFilterOrSelectFilter = (
    filterData: FilterGroup[],
    isApply = true
  ) => {
    const { isIPPChanged, newValue } = checkIppChanged(
      productCatalog.filterData,
      filterData
    );
    let plan = selectedPlan;
    if (isIPPChanged) {
      plan = getPlanWhenChangeIpp(newValue);
    }
    if (isApply) {
      return dispatch(actionApplyFilter({ filterData, selectedPlan: plan }));
    }
    return dispatch(actionSelectFilter({ filterData, selectedPlan: plan }));
  };

  const clearFilter = () => {
    dispatch(actionClearFilter());
  };

  const onCloseFilterModal = () => {
    dispatch(actionResetToPreviousFilter());
  };

  const renderPromoStrip = () => {
    const promoStrip = getAemComponentByName(allAemPages, 'PromoStrip');

    if (!promoStrip?.enablePromoStrip) {
      return null;
    }

    const { primaryLiner } = promoStrip;

    return (
      <FullWidthDiv>
        <StatusAlert bodyContent={primaryLiner} title={''} type="info" />
      </FullWidthDiv>
    );
  };

  const renderCisPointOfEntry = () => {
    if (cisLoading || cisRates) {
      return null;
    }
    return <CisPointOfEntry />;
  };

  const renderBanner = () => {
    const flowType = isCisPage
      ? BANNER_FLOW_TYPE.CIS_CATALOG
      : BANNER_FLOW_TYPE.RES_CATALOG;

    const promoBanner = getPhoneCatalogBanner(banners, flowType);
    return promoBanner ? <BannerList banners={promoBanner} /> : <></>;
  };

  const renderCatalog = () => {
    return (
      <Spacing top={0}>
        <Spacing bottom={5}>
          {renderPromoStrip()}
          {renderBanner()}
          {renderCisPointOfEntry()}
          <Grid>
            <Row>
              <Column>
                <ProductCatalogue
                  data={{
                    productList: productCatalog.productList,
                    filterData: productCatalog.filterData,
                    productCatalogTitle: t('PRODUCT_CATALOG_TITLE') as string
                  }}
                  configs={{
                    enableViewMoreBtn: false,
                    enableFilter: true,
                    enableSorter: false,
                    enablePromoStrip: false,
                    enableBanner: false,
                    hasProductCount: true,
                    hideDisabledFilter: true,
                    anchorTagPath: `${process.env.GATSBY_HOST}/${CONSTANTS.URL_PATHS.PHONE}`
                  }}
                  localisation={{
                    SEARCH_MODAL_TITLE: t('SEARCH_MODAL_TITLE')
                  }}
                  callbacks={{
                    onProductClick: product => {
                      if (product.productId !== selectedProduct?.deviceId) {
                        dispatch(accessoryActions.clearSelectedAccessories());
                      }
                    },
                    applyFilter: filterData => {
                      applyFilterOrSelectFilter(filterData);
                    },
                    clearFilter,
                    onSelectFilter: filterData => {
                      applyFilterOrSelectFilter(filterData, false);
                    },
                    onCloseFilterModal
                  }}
                />
              </Column>
            </Row>
          </Grid>
        </Spacing>
      </Spacing>
    );
  };

  return <>{renderCatalog()}</>;
};
